/**
 * Per-page, per-language SEO titles and descriptions for pages that previously
 * had hardcoded Polish metadata (or none at all).
 */
export type PageSeo = { title: string; description: string };

type Key =
  | "cart"
  | "checkout"
  | "privacy"
  | "cookies"
  | "terms"
  | "withdrawal"
  | "account"
  | "auth"
  | "quickPayment"
  | "paymentStatus"
  | "notFound";

export const pageSeo: Record<string, Record<Key, PageSeo>> = {
  pl: {
    cart: {
      title: "Koszyk – Twoje naklejki | Stickers Tattooo",
      description:
        "Sprawdź naklejki w koszyku, wybierz dostawę i pobierz ofertę w PDF. Bezpieczna płatność online w Stickers Tattooo.",
    },
    checkout: {
      title: "Zamówienie i płatność – Stickers Tattooo",
      description:
        "Dokończ zamówienie naklejek: dane do faktury, adres dostawy, paczkomat lub kurier i szybka płatność online.",
    },
    privacy: {
      title: "Polityka prywatności – Stickers Tattooo",
      description:
        "Jak przetwarzamy i chronimy Twoje dane osobowe zgodnie z RODO w sklepie z naklejkami stickerstattooo.pl.",
    },
    cookies: {
      title: "Polityka cookies – Stickers Tattooo",
      description:
        "Jakich plików cookie używamy w sklepie Stickers Tattooo, w jakim celu i jak zarządzać zgodami w przeglądarce.",
    },
    terms: {
      title: "Regulamin sklepu – Stickers Tattooo Katowice",
      description:
        "Zasady zamawiania naklejek, płatności, dostawy, reklamacji i zwrotów w sklepie Stickers Tattooo (DRUKEXPERT.PL Sp. z o.o.).",
    },
    withdrawal: {
      title: "Prawo odstąpienia od umowy – Stickers Tattooo",
      description:
        "Kiedy przysługuje odstąpienie od umowy, wzór formularza i wyjątki dla naklejek personalizowanych.",
    },
    account: {
      title: "Moje konto – Stickers Tattooo",
      description:
        "Zarządzaj zamówieniami, adresami, punktami lojalnościowymi i zapisanymi konfiguracjami naklejek.",
    },
    auth: {
      title: "Logowanie i rejestracja – Stickers Tattooo",
      description:
        "Zaloguj się lub załóż konto, aby śledzić zamówienia naklejek, zbierać punkty i zapisywać konfiguracje.",
    },
    quickPayment: {
      title: "Szybka płatność za zamówienie – Stickers Tattooo",
      description:
        "Opłać ustalone zamówienie naklejek online: wpisz kwotę, tytuł przelewu i zapłać w kilka sekund.",
    },
    paymentStatus: {
      title: "Status płatności – Stickers Tattooo",
      description: "Sprawdź status płatności za swoje zamówienie naklejek w Stickers Tattooo.",
    },
    notFound: {
      title: "Strona nie znaleziona (404) – Stickers Tattooo",
      description: "Ta strona nie istnieje. Wróć na stronę główną drukarni naklejek Stickers Tattooo.",
    },
  },
  en: {
    cart: {
      title: "Cart – Your custom stickers | Stickers Tattooo",
      description:
        "Review the stickers in your cart, choose delivery and download a PDF quote. Secure online payment at Stickers Tattooo.",
    },
    checkout: {
      title: "Checkout and payment – Stickers Tattooo",
      description:
        "Complete your sticker order: billing details, delivery address, courier or parcel locker and fast online payment.",
    },
    privacy: {
      title: "Privacy policy – Stickers Tattooo",
      description:
        "How we process and protect your personal data under GDPR at the stickerstattooo.pl sticker shop.",
    },
    cookies: {
      title: "Cookie policy – Stickers Tattooo",
      description:
        "Which cookies Stickers Tattooo uses, what they are for and how to manage your consent in the browser.",
    },
    terms: {
      title: "Terms and conditions – Stickers Tattooo",
      description:
        "Rules for ordering stickers, payments, delivery, complaints and returns at Stickers Tattooo (DRUKEXPERT.PL Sp. z o.o.).",
    },
    withdrawal: {
      title: "Right of withdrawal – Stickers Tattooo",
      description:
        "When you can withdraw from the contract, the withdrawal form template and exceptions for personalised stickers.",
    },
    account: {
      title: "My account – Stickers Tattooo",
      description:
        "Manage your orders, addresses, loyalty points and saved sticker configurations.",
    },
    auth: {
      title: "Sign in or create an account – Stickers Tattooo",
      description:
        "Log in or register to track sticker orders, collect loyalty points and save your configurations.",
    },
    quickPayment: {
      title: "Quick payment for your order – Stickers Tattooo",
      description:
        "Pay for an agreed sticker order online: enter the amount, payment title and pay in seconds.",
    },
    paymentStatus: {
      title: "Payment status – Stickers Tattooo",
      description: "Check the payment status of your sticker order at Stickers Tattooo.",
    },
    notFound: {
      title: "Page not found (404) – Stickers Tattooo",
      description: "This page does not exist. Go back to the Stickers Tattooo home page.",
    },
  },
  de: {
    cart: {
      title: "Warenkorb – Ihre Aufkleber | Stickers Tattooo",
      description:
        "Prüfen Sie die Aufkleber im Warenkorb, wählen Sie den Versand und laden Sie ein PDF-Angebot herunter. Sichere Online-Zahlung.",
    },
    checkout: {
      title: "Bestellung und Zahlung – Stickers Tattooo",
      description:
        "Schließen Sie Ihre Aufkleber-Bestellung ab: Rechnungsdaten, Lieferadresse, Kurier oder Paketstation und schnelle Online-Zahlung.",
    },
    privacy: {
      title: "Datenschutzerklärung – Stickers Tattooo",
      description:
        "Wie wir Ihre personenbezogenen Daten gemäß DSGVO im Aufkleber-Shop stickerstattooo.pl verarbeiten und schützen.",
    },
    cookies: {
      title: "Cookie-Richtlinie – Stickers Tattooo",
      description:
        "Welche Cookies Stickers Tattooo verwendet, wozu sie dienen und wie Sie Ihre Einwilligung verwalten.",
    },
    terms: {
      title: "AGB des Shops – Stickers Tattooo",
      description:
        "Regeln für Bestellung, Zahlung, Lieferung, Reklamation und Rückgabe von Aufklebern bei Stickers Tattooo.",
    },
    withdrawal: {
      title: "Widerrufsrecht – Stickers Tattooo",
      description:
        "Wann Sie vom Vertrag zurücktreten können, Musterformular und Ausnahmen für personalisierte Aufkleber.",
    },
    account: {
      title: "Mein Konto – Stickers Tattooo",
      description:
        "Verwalten Sie Bestellungen, Adressen, Treuepunkte und gespeicherte Aufkleber-Konfigurationen.",
    },
    auth: {
      title: "Anmelden oder registrieren – Stickers Tattooo",
      description:
        "Melden Sie sich an oder erstellen Sie ein Konto, um Bestellungen zu verfolgen und Punkte zu sammeln.",
    },
    quickPayment: {
      title: "Schnelle Zahlung der Bestellung – Stickers Tattooo",
      description:
        "Bezahlen Sie eine vereinbarte Aufkleber-Bestellung online: Betrag und Verwendungszweck eingeben und zahlen.",
    },
    paymentStatus: {
      title: "Zahlungsstatus – Stickers Tattooo",
      description: "Prüfen Sie den Zahlungsstatus Ihrer Aufkleber-Bestellung bei Stickers Tattooo.",
    },
    notFound: {
      title: "Seite nicht gefunden (404) – Stickers Tattooo",
      description: "Diese Seite existiert nicht. Zurück zur Startseite von Stickers Tattooo.",
    },
  },
  fr: {
    cart: {
      title: "Panier – Vos autocollants | Stickers Tattooo",
      description:
        "Vérifiez les autocollants de votre panier, choisissez la livraison et téléchargez un devis PDF. Paiement en ligne sécurisé.",
    },
    checkout: {
      title: "Commande et paiement – Stickers Tattooo",
      description:
        "Finalisez votre commande d'autocollants : facturation, adresse de livraison, transporteur ou point relais et paiement rapide.",
    },
    privacy: {
      title: "Politique de confidentialité – Stickers Tattooo",
      description:
        "Comment nous traitons et protégeons vos données personnelles conformément au RGPD sur stickerstattooo.pl.",
    },
    cookies: {
      title: "Politique de cookies – Stickers Tattooo",
      description:
        "Quels cookies Stickers Tattooo utilise, à quoi ils servent et comment gérer votre consentement.",
    },
    terms: {
      title: "Conditions générales – Stickers Tattooo",
      description:
        "Règles de commande, de paiement, de livraison, de réclamation et de retour des autocollants chez Stickers Tattooo.",
    },
    withdrawal: {
      title: "Droit de rétractation – Stickers Tattooo",
      description:
        "Quand vous pouvez vous rétracter, modèle de formulaire et exceptions pour les autocollants personnalisés.",
    },
    account: {
      title: "Mon compte – Stickers Tattooo",
      description:
        "Gérez vos commandes, adresses, points de fidélité et configurations d'autocollants enregistrées.",
    },
    auth: {
      title: "Connexion et inscription – Stickers Tattooo",
      description:
        "Connectez-vous ou créez un compte pour suivre vos commandes, cumuler des points et sauvegarder vos configurations.",
    },
    quickPayment: {
      title: "Paiement rapide de la commande – Stickers Tattooo",
      description:
        "Payez en ligne une commande d'autocollants convenue : indiquez le montant et le libellé, puis payez.",
    },
    paymentStatus: {
      title: "Statut du paiement – Stickers Tattooo",
      description: "Vérifiez le statut du paiement de votre commande d'autocollants.",
    },
    notFound: {
      title: "Page introuvable (404) – Stickers Tattooo",
      description: "Cette page n'existe pas. Retournez à l'accueil de Stickers Tattooo.",
    },
  },
  it: {
    cart: {
      title: "Carrello – I tuoi adesivi | Stickers Tattooo",
      description:
        "Controlla gli adesivi nel carrello, scegli la spedizione e scarica il preventivo in PDF. Pagamento online sicuro.",
    },
    checkout: {
      title: "Ordine e pagamento – Stickers Tattooo",
      description:
        "Completa l'ordine di adesivi: dati di fatturazione, indirizzo di consegna, corriere o locker e pagamento rapido.",
    },
    privacy: {
      title: "Informativa sulla privacy – Stickers Tattooo",
      description:
        "Come trattiamo e proteggiamo i tuoi dati personali secondo il GDPR sul sito stickerstattooo.pl.",
    },
    cookies: {
      title: "Informativa sui cookie – Stickers Tattooo",
      description:
        "Quali cookie utilizza Stickers Tattooo, a cosa servono e come gestire il consenso nel browser.",
    },
    terms: {
      title: "Termini e condizioni – Stickers Tattooo",
      description:
        "Regole per ordini, pagamenti, spedizioni, reclami e resi degli adesivi presso Stickers Tattooo.",
    },
    withdrawal: {
      title: "Diritto di recesso – Stickers Tattooo",
      description:
        "Quando è possibile recedere dal contratto, modulo di recesso ed eccezioni per gli adesivi personalizzati.",
    },
    account: {
      title: "Il mio account – Stickers Tattooo",
      description:
        "Gestisci ordini, indirizzi, punti fedeltà e configurazioni di adesivi salvate.",
    },
    auth: {
      title: "Accedi o registrati – Stickers Tattooo",
      description:
        "Accedi o crea un account per seguire gli ordini, raccogliere punti e salvare le configurazioni.",
    },
    quickPayment: {
      title: "Pagamento rapido dell'ordine – Stickers Tattooo",
      description:
        "Paga online un ordine di adesivi concordato: inserisci importo e causale e paga in pochi secondi.",
    },
    paymentStatus: {
      title: "Stato del pagamento – Stickers Tattooo",
      description: "Controlla lo stato del pagamento del tuo ordine di adesivi.",
    },
    notFound: {
      title: "Pagina non trovata (404) – Stickers Tattooo",
      description: "Questa pagina non esiste. Torna alla home page di Stickers Tattooo.",
    },
  },
};

export function getPageSeo(lang: string, key: Key): PageSeo {
  return (pageSeo[lang] ?? pageSeo.pl)[key] ?? pageSeo.pl[key];
}
